import { computed, readonly, ref, type ComputedRef } from "vue";
import type { FormInquiryKind, InquiryLead, InquirySend } from "@ai-platform/ui/inquiry.ts";
import { inquiryFailureText, inquiryRefusedFields } from "../api/failure.ts";
import { useRouteMutation } from "../api/route-query.ts";
import { can } from "../session/access.ts";
import { useSession } from "../session/session.ts";

/*
 * Inquiries from the portal (UC-30, ADR-093, AP-F6.5): the one mutation, the forms' way to it, and
 * the state of the one inquiry dialog in `App.vue`. User menu and product page open it with the
 * kinds they offer. A guest's way in (header, sidebar, product page, login) leads to the
 * application page `/beta` instead (ADR-143 addendum 3, TL 2026-10-28); the dialog can still show
 * the application (`APPLICATION`), which no entry opens now: it stays for when the design changes
 * back (TL 2026-10-28). The dialog is browser state, not server data, so it lives here and not in
 * the query cache (ADR-076).
 */

/** `POST /api/inquiries`, for guests (`access`, `application`) and customers (`inquiry.create`). */
export const useCreateInquiry = () => useRouteMutation("inquiryCreate");

/**
 * How `InquiryForm` and `ApplicationForm` of `packages/ui` send, in the inquiry dialog and on the
 * application page: through the typed client; a failure says what `inquiryFailureText` says, and a
 * `400` hands the form the fields it names, for the form to mark them.
 */
export function useInquirySend(): InquirySend {
  const create = useCreateInquiry();
  return async (body) => {
    try {
      await create.mutateAsync({ body });
      return { ok: true };
    } catch (error) {
      const fields = inquiryRefusedFields(error);
      return { ok: false, message: inquiryFailureText(error, body.kind), ...(fields.length ? { fields } : {}) };
    }
  };
}

type Kinds = readonly [FormInquiryKind, ...FormInquiryKind[]];

/** What a guest asks for about a product only offered on request (ADR-093 point 8, ADR-112 point 6). */
export const ACCESS_KINDS: Kinds = ["access"];

/** What a customer writes to the team about, the first preselected; the prototype's order (B-123, 2026-10-19). */
export const CUSTOMER_KINDS: Kinds = ["support", "workflow", "callback"];

/** A customer's inquiry about a product on request: a support inquiry, no choice (ADR-112 point 6). */
export const SUPPORT_KINDS: Kinds = ["support"];

export type InquiryRequest = {
  kinds: Kinds;
  /** the kind preselected instead of the first, e.g. by the overview's team card (B-55) */
  kind?: FormInquiryKind | undefined;
  /** preselected, e.g. on a product page */
  productId?: string | undefined;
  /** the entry, when it says more than the kind, e.g. a product that is only offered on request */
  lead?: InquiryLead | undefined;
};

/**
 * The application for the beta (`ApplicationForm`) in the dialog; after a login refused for an
 * instance not enabled with `lead: "instance"`, whose intro says why (ADR-093 point 8). No entry
 * opens it since the application page (ADR-143 addendum 3); kept for a change of design (TL
 * 2026-10-28).
 */
export type ApplicationRequest = { form: "application"; lead?: "instance" | undefined };

/** The application for the beta in the dialog, see `ApplicationRequest`. */
export const APPLICATION: ApplicationRequest = { form: "application" };

/** What the dialog opens with: the inquiry form with its kinds, or the application (`form`). */
export type DialogRequest = InquiryRequest | ApplicationRequest;

const current = ref<DialogRequest | null>(null);
/** Counts the openings, so every one starts with a fresh form. */
const openings = ref(0);

/** The inquiry dialog: what it was opened with, or `null` while it is closed. */
export function useInquiryDialog() {
  return {
    request: readonly(current),
    openings: readonly(openings),
    open(request: DialogRequest) {
      openings.value += 1;
      current.value = request;
    },
    close() {
      current.value = null;
    },
  };
}

/**
 * "Gespräch vereinbaren" as the prototype's `talkCall`: an access inquiry about a talk for guests, a
 * callback for sessions that may write (`inquiry.create`); `null` while the session loads and for
 * anyone else, who gets no way to it.
 */
export function useTalkRequest(): ComputedRef<InquiryRequest | null> {
  const { session, isPending } = useSession();
  return computed(() => {
    if (isPending.value) return null;
    if (!session.value) return { kinds: ACCESS_KINDS, lead: "talk" };
    return can(session.value, "inquiry.create") ? { kinds: CUSTOMER_KINDS, kind: "callback" } : null;
  });
}

/**
 * "Anfrage senden" about a family on request, as the prototype's family pages (`vAgents`,
 * `vPixies`: `openRequest(isCust ? 'support' : 'access', { lead:'product' })`): an access inquiry for
 * guests, a question for sessions that may write, both with the entry `product`, which promises an
 * offer (ADR-093, ADR-150 point 4); `null` while the session loads and for anyone else, as staff.
 */
export function useOfferRequest(): ComputedRef<InquiryRequest | null> {
  const { session, isPending } = useSession();
  return computed(() => {
    if (isPending.value) return null;
    if (!session.value) return { kinds: ACCESS_KINDS, lead: "product" };
    return can(session.value, "inquiry.create") ? { kinds: SUPPORT_KINDS, lead: "product" } : null;
  });
}
